<div class="msa-container">

  <h1>Master Service Agreement</h1>
  <p class="msa-subtitle">Cloud-Based Donation Management Platform</p>
  <p class="msa-meta">
    <strong>xoomtag llc, doing business as Chareety</strong><br>
    Bartlett, IL 60103<br>
    <a href="mailto:support@chareety.com">support&#64;chareety.com</a>
  </p>
  <p class="msa-meta"><em>Last updated: May 2026</em></p>

  <hr>

  <!-- TABLE OF CONTENTS -->
  <nav class="msa-toc">
    <h2>Table of Contents</h2>
    <ol>
      <li><a (click)="scrollTo('definitions')">Definitions</a></li>
      <li><a (click)="scrollTo('services-provided')">Services Provided</a></li>
      <li><a (click)="scrollTo('account-setup')">Account Setup and Onboarding</a></li>
      <li><a (click)="scrollTo('org-responsibilities')">Organization Responsibilities</a></li>
      <li><a (click)="scrollTo('fees-payment')">Fees and Payment</a></li>
      <li><a (click)="scrollTo('data-ownership')">Data Ownership and Processing</a></li>
      <li><a (click)="scrollTo('confidentiality')">Confidentiality</a></li>
      <li><a (click)="scrollTo('intellectual-property')">Intellectual Property</a></li>
      <li><a (click)="scrollTo('representations-warranties')">Representations and Warranties</a></li>
      <li><a (click)="scrollTo('limitation-liability')">Limitation of Liability</a></li>
      <li><a (click)="scrollTo('indemnification')">Indemnification</a></li>
      <li><a (click)="scrollTo('term')">Term</a></li>
      <li><a (click)="scrollTo('termination')">Termination</a></li>
      <li><a (click)="scrollTo('effect-termination')">Effect of Termination</a></li>
      <li><a (click)="scrollTo('support-sla')">Support and Service Level</a></li>
      <li><a (click)="scrollTo('modifications')">Modifications to Agreement</a></li>
      <li><a (click)="scrollTo('governing-law')">Governing Law and Dispute Resolution</a></li>
      <li><a (click)="scrollTo('general-provisions')">General Provisions</a></li>
    </ol>
    <p class="msa-toc-exhibits">Exhibits</p>
    <ol type="A">
      <li><a (click)="scrollTo('exhibit-a')">Pricing Schedule</a></li>
      <li><a (click)="scrollTo('exhibit-b')">Data Processing Addendum (DPA)</a></li>
    </ol>
  </nav>

  <hr>

  <p>
    This Master Service Agreement ("Agreement") is entered into by and between
    <strong>xoomtag llc</strong>, a limited liability company doing business as
    <strong>Chareety</strong> ("Chareety," "we," "us," or "our"), and the nonprofit
    organization or entity ("Organization," "you," or "your") that accepts this
    Agreement by completing the onboarding process or by clicking "I Agree" or a
    similar button presented with this Agreement. By accepting this Agreement, you
    represent that you have the legal authority to bind the Organization to these terms.
  </p>

  <hr>

  <!-- SECTION 1: DEFINITIONS -->
  <h2 id="definitions">1. Definitions</h2>

  <p>The following terms, when capitalized in this Agreement, have the meanings set forth below:</p>

  <dl>
    <dt>"Platform"</dt>
    <dd>The Chareety SaaS platform, including but not limited to OrgConsole, DonationKiosk, donor-facing applications (DonorApp, MyChareety), and all related web and mobile interfaces.</dd>

    <dt>"Organization" or "You"</dt>
    <dd>The nonprofit entity that enters into this Agreement and uses the Platform.</dd>

    <dt>"Chareety" or "We"</dt>
    <dd>xoomtag llc, a limited liability company doing business as Chareety.</dd>

    <dt>"Donor Data"</dt>
    <dd>Personal information of donors collected through the Platform, including but not limited to names, contact information, donation history, payment information, and communication preferences.</dd>

    <dt>"Spoke Project"</dt>
    <dd>The isolated Firebase project provisioned exclusively for the Organization, containing the Organization's own database, authentication system, and administrative console.</dd>

    <dt>"Services"</dt>
    <dd>The services provided by Chareety through the Platform, including donation processing, kiosk management, donor management, analytics, reporting, and email/SMS broadcast capabilities.</dd>

    <dt>"Order Form"</dt>
    <dd>A document or electronic form that specifies the Services, fees, and other commercial terms agreed upon between the parties, applicable to Enterprise tier customers.</dd>
  </dl>

  <!-- SECTION 2: SERVICES PROVIDED -->
  <h2 id="services-provided">2. Services Provided</h2>

  <p>Subject to the terms of this Agreement, Chareety shall provide the Organization with the following Services:</p>

  <ol type="a">
    <li>
      <strong>Cloud-Based Donation Platform.</strong> Chareety provides a cloud-based platform
      for donation collection, management, and reporting.
    </li>
    <li>
      <strong>Isolated Environment.</strong> Each Organization receives an isolated environment
      (Spoke Project) with its own database, authentication system, and administrative console
      (OrgConsole). This ensures that the Organization's data is logically separated from other
      organizations on the Platform.
    </li>
    <li>
      <strong>DonationKiosk.</strong> Hardware devices for in-person donation collection,
      available for purchase separately. Kiosk devices connect to the Organization's Spoke
      Project for secure transaction processing.
    </li>
    <li>
      <strong>Donor Management.</strong> Tools for managing donor records, viewing donation
      history, generating analytics, and producing reports via OrgConsole.
    </li>
    <li>
      <strong>Broadcast Capabilities.</strong> Email and SMS broadcast features for donor
      communications, pledge reminders, and marketing campaigns.
    </li>
    <li>
      <strong>Payment Processing.</strong> Integration with Stripe for secure processing of
      one-time and recurring donations. Stripe's terms of service apply to all payment
      processing activities.
    </li>
  </ol>

  <!-- SECTION 3: ACCOUNT SETUP AND ONBOARDING -->
  <h2 id="account-setup">3. Account Setup and Onboarding</h2>

  <ol type="a">
    <li>
      <strong>Onboarding Process.</strong> The Organization shall complete the onboarding
      process via <strong>onboard.chareety.com</strong>, providing all required information
      accurately and completely.
    </li>
    <li>
      <strong>Required Information.</strong> The Organization must provide accurate information
      including, but not limited to, its Employer Identification Number (EIN), legal name,
      mailing address, and authorized representative details.
    </li>
    <li>
      <strong>Spoke Provisioning.</strong> Upon successful completion of onboarding, Chareety
      will provision a dedicated Spoke Project for the Organization. Provisioning timelines are
      subject to verification of the Organization's information.
    </li>
    <li>
      <strong>Admin Access.</strong> The Organization's designated administrator will receive
      access credentials to OrgConsole upon completion of provisioning.
    </li>
  </ol>

  <!-- SECTION 4: ORGANIZATION RESPONSIBILITIES -->
  <h2 id="org-responsibilities">4. Organization Responsibilities</h2>

  <p>The Organization agrees to:</p>

  <ol type="a">
    <li>
      Maintain accurate and up-to-date account information, including contact details,
      tax-exempt status documentation, and authorized user lists.
    </li>
    <li>
      Comply with all applicable federal, state, and local laws, including but not limited to
      laws governing tax-exempt status, IRS reporting requirements, and state charitable
      solicitation registration requirements.
    </li>
    <li>
      Ensure that all authorized users are properly trained in the use of the Platform and
      understand their responsibilities regarding donor data and privacy.
    </li>
    <li>
      Not misuse the Platform, including but not limited to attempting unauthorized access to
      other organizations' data, reverse engineering the Platform, or using the Platform for
      any unlawful purpose.
    </li>
    <li>
      Be solely responsible for the accuracy of tax receipts issued to donors through the
      Platform. Chareety provides the tools for receipt generation but does not verify the
      tax-deductibility of donations.
    </li>
    <li>
      Comply with Stripe's terms of service and acceptable use policies for all payment
      processing activities conducted through the Platform.
    </li>
    <li>
      Promptly notify Chareety of any security incidents, unauthorized access, or suspected
      breaches affecting the Organization's account or data.
    </li>
  </ol>

  <!-- SECTION 5: FEES AND PAYMENT -->
  <h2 id="fees-payment">5. Fees and Payment</h2>

  <ol type="a">
    <li>
      <strong>Applicable Fees.</strong> The Organization shall pay the fees set forth in
      <a (click)="scrollTo('exhibit-a')"><strong>Exhibit A (Pricing Schedule)</strong></a>,
      as applicable to the Organization's selected service tier.
    </li>
    <li>
      <strong>Billing Cycle.</strong> Fees are billed monthly in arrears via Stripe auto-charge
      to the payment method on file. Usage is calculated at the end of each billing period.
    </li>
    <li>
      <strong>Pricing Updates.</strong> Chareety may update pricing with no less than thirty
      (30) days' prior written notice to the Organization. Continued use of the Platform after
      the effective date of a pricing change constitutes acceptance of the updated pricing.
    </li>
    <li>
      <strong>No Refunds.</strong> There are no refunds for partial months of service. If the
      Organization terminates mid-cycle, the Organization remains responsible for fees accrued
      through the end of the current billing period.
    </li>
    <li>
      <strong>Late Payment.</strong> If payment is not received within fifteen (15) days of
      the due date, Chareety may suspend the Organization's access to the Platform upon
      written notice. Access will be restored upon receipt of all outstanding payments.
    </li>
    <li>
      <strong>Taxes.</strong> All fees are exclusive of taxes. The Organization is responsible
      for all applicable taxes, excluding taxes based on Chareety's net income.
    </li>
  </ol>

  <!-- SECTION 6: DATA OWNERSHIP AND PROCESSING -->
  <h2 id="data-ownership">6. Data Ownership and Processing</h2>

  <ol type="a">
    <li>
      <strong>Data Controller.</strong> The Organization is the Data Controller with respect
      to all Donor Data collected through the Platform. The Organization determines the
      purposes and means of processing Donor Data.
    </li>
    <li>
      <strong>Data Processor.</strong> Chareety acts as the Data Processor, processing Donor
      Data solely on behalf of and as instructed by the Organization, and as necessary to
      provide the Services described in this Agreement.
    </li>
    <li>
      <strong>Data Ownership.</strong> The Organization owns all Donor Data collected through
      the Platform. Chareety does not claim ownership of Donor Data.
    </li>
    <li>
      <strong>Hub Records.</strong> Chareety retains a copy of donation records in the hub
      database for the purpose of providing donors with cross-organization donation history
      via MyChareety (the donor portal). This retention is necessary to deliver the Services
      and does not constitute a transfer of data ownership.
    </li>
    <li>
      <strong>No Sale of Data.</strong> Chareety will not sell, rent, or share Donor Data with
      third parties except as strictly necessary to provide the Services (e.g., transmitting
      payment data to Stripe for transaction processing).
    </li>
    <li>
      <strong>Data Processing Addendum.</strong> The detailed terms governing data processing
      are set forth in <a (click)="scrollTo('exhibit-b')"><strong>Exhibit B (Data Processing
      Addendum)</strong></a>, which is incorporated into this Agreement by reference.
    </li>
  </ol>

  <!-- SECTION 7: CONFIDENTIALITY -->
  <h2 id="confidentiality">7. Confidentiality</h2>

  <ol type="a">
    <li>
      <strong>Confidential Information.</strong> Each party acknowledges that it may receive
      or have access to information that is proprietary or confidential to the other party
      ("Confidential Information"). Confidential Information includes, without limitation:
      business plans, donor data, financial information, platform internals, technical
      specifications, pricing, and any information marked or reasonably understood to be
      confidential.
    </li>
    <li>
      <strong>Obligations.</strong> Each party agrees to: (i) hold Confidential Information in
      strict confidence; (ii) not disclose Confidential Information to any third party except
      as permitted under this Agreement; and (iii) use Confidential Information solely for the
      purpose of performing its obligations or exercising its rights under this Agreement.
    </li>
    <li>
      <strong>Exceptions.</strong> Confidential Information does not include information that:
      (i) is or becomes publicly available through no fault of the receiving party;
      (ii) was known to the receiving party prior to disclosure; (iii) is independently
      developed by the receiving party without use of Confidential Information; or (iv) is
      required to be disclosed by law, regulation, or court order, provided that the receiving
      party gives reasonable notice to the disclosing party where permitted.
    </li>
    <li>
      <strong>Survival.</strong> The obligations of this Section 7 shall survive termination
      of this Agreement for a period of three (3) years.
    </li>
  </ol>

  <!-- SECTION 8: INTELLECTUAL PROPERTY -->
  <h2 id="intellectual-property">8. Intellectual Property</h2>

  <ol type="a">
    <li>
      <strong>Platform IP.</strong> Chareety owns all intellectual property rights in and to
      the Platform, including but not limited to software, source code, algorithms, user
      interfaces, branding, documentation, and all improvements, modifications, and derivative
      works thereof. Nothing in this Agreement transfers any intellectual property rights in
      the Platform to the Organization.
    </li>
    <li>
      <strong>Organization Content.</strong> The Organization retains all ownership rights in
      its own content, including logos, branding materials, and other assets uploaded to the
      Platform.
    </li>
    <li>
      <strong>License to Display.</strong> The Organization grants Chareety a limited,
      non-exclusive, royalty-free license to display the Organization's branding and content
      within the Platform solely for the purpose of providing the Services.
    </li>
    <li>
      <strong>No Transfer.</strong> Except as expressly stated in this Agreement, neither party
      grants the other any rights, licenses, or interests in its intellectual property.
    </li>
  </ol>

  <!-- SECTION 9: REPRESENTATIONS AND WARRANTIES -->
  <h2 id="representations-warranties">9. Representations and Warranties</h2>

  <ol type="a">
    <li>
      <strong>Chareety Warranties.</strong> Chareety represents and warrants that:
      <ol type="i">
        <li>The Platform will perform materially in accordance with its published documentation.</li>
        <li>Chareety will employ commercially reasonable security measures to protect Donor Data.</li>
        <li>Chareety has the legal authority to enter into and perform this Agreement.</li>
      </ol>
    </li>
    <li>
      <strong>Organization Warranties.</strong> The Organization represents and warrants that:
      <ol type="i">
        <li>The Organization has the legal authority to enter into this Agreement and that the
          individual accepting this Agreement is duly authorized to bind the Organization.</li>
        <li>The Organization's tax-exempt status under Section 501(c)(3) of the Internal Revenue
          Code (or applicable provision) is valid and in good standing.</li>
        <li>The Organization will comply with all applicable laws and regulations in its use of
          the Platform.</li>
      </ol>
    </li>
    <li>
      <strong>DISCLAIMER.</strong> EXCEPT FOR THE EXPRESS WARRANTIES SET FORTH IN THIS
      SECTION 9, THE PLATFORM AND SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE."
      CHAREETY DISCLAIMS ALL OTHER WARRANTIES, WHETHER EXPRESS, IMPLIED, OR STATUTORY,
      INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
      PARTICULAR PURPOSE, AND NON-INFRINGEMENT. CHAREETY DOES NOT WARRANT THAT THE PLATFORM
      WILL BE UNINTERRUPTED, ERROR-FREE, OR COMPLETELY SECURE.
    </li>
  </ol>

  <!-- SECTION 10: LIMITATION OF LIABILITY -->
  <h2 id="limitation-liability">10. Limitation of Liability</h2>

  <ol type="a">
    <li>
      <strong>Exclusion of Damages.</strong> TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW,
      NEITHER PARTY SHALL BE LIABLE TO THE OTHER PARTY FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
      CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, LOSS
      OF DATA, LOSS OF BUSINESS OPPORTUNITY, OR LOSS OF GOODWILL, ARISING OUT OF OR RELATED
      TO THIS AGREEMENT, REGARDLESS OF THE THEORY OF LIABILITY (CONTRACT, TORT, STRICT
      LIABILITY, OR OTHERWISE) AND EVEN IF THE PARTY HAS BEEN ADVISED OF THE POSSIBILITY OF
      SUCH DAMAGES.
    </li>
    <li>
      <strong>Cap on Liability.</strong> CHAREETY'S TOTAL AGGREGATE LIABILITY ARISING OUT OF
      OR RELATED TO THIS AGREEMENT SHALL NOT EXCEED THE TOTAL FEES PAID BY THE ORGANIZATION
      TO CHAREETY IN THE TWELVE (12) MONTHS IMMEDIATELY PRECEDING THE EVENT GIVING RISE TO
      THE CLAIM.
    </li>
    <li>
      <strong>Exclusions.</strong> The limitations set forth in this Section 10 shall not apply
      to: (i) liability arising from fraud or willful misconduct; (ii) liability arising from
      a data breach caused by a party's gross negligence; or (iii) indemnification obligations
      under Section 11.
    </li>
  </ol>

  <!-- SECTION 11: INDEMNIFICATION -->
  <h2 id="indemnification">11. Indemnification</h2>

  <ol type="a">
    <li>
      <strong>By Organization.</strong> The Organization shall indemnify, defend, and hold
      harmless Chareety and its officers, directors, employees, and agents from and against
      any and all claims, losses, damages, liabilities, costs, and expenses (including
      reasonable attorneys' fees) arising out of or related to: (i) the Organization's misuse
      of the Platform; (ii) the Organization's violation of applicable laws or regulations;
      or (iii) disputes between the Organization and its donors.
    </li>
    <li>
      <strong>By Chareety.</strong> Chareety shall indemnify, defend, and hold harmless the
      Organization and its officers, directors, employees, and agents from and against any
      and all claims, losses, damages, liabilities, costs, and expenses (including reasonable
      attorneys' fees) arising out of a third-party claim that the Platform infringes such
      third party's intellectual property rights, provided that the Organization promptly
      notifies Chareety of such claim and cooperates with Chareety's defense.
    </li>
    <li>
      <strong>Procedure.</strong> The indemnified party shall: (i) provide prompt written
      notice of the claim; (ii) grant the indemnifying party sole control of the defense and
      settlement; and (iii) provide reasonable cooperation at the indemnifying party's expense.
    </li>
  </ol>

  <!-- SECTION 12: TERM -->
  <h2 id="term">12. Term</h2>

  <ol type="a">
    <li>
      <strong>Free and Premium Tiers.</strong> For Organizations on the Free or Premium tier,
      this Agreement is effective upon acceptance and continues on a month-to-month basis
      until terminated in accordance with Section 13.
    </li>
    <li>
      <strong>Enterprise Tier.</strong> For Organizations on the Enterprise tier, the initial
      term shall be as specified in the applicable Order Form. Enterprise agreements
      automatically renew for successive periods of equal length unless either party provides
      written notice of non-renewal at least thirty (30) days prior to the end of the
      then-current term.
    </li>
    <li>
      <strong>Auto-Renewal.</strong> This Agreement automatically renews at the end of each
      term (monthly for Free/Premium, as specified for Enterprise) unless terminated in
      accordance with this Agreement.
    </li>
  </ol>

  <!-- SECTION 13: TERMINATION -->
  <h2 id="termination">13. Termination</h2>

  <ol type="a">
    <li>
      <strong>Termination for Convenience.</strong> Either party may terminate this Agreement
      for any reason by providing the other party with at least thirty (30) days' prior
      written notice.
    </li>
    <li>
      <strong>Termination by Chareety.</strong> Chareety may terminate this Agreement
      immediately upon written notice if the Organization: (i) fails to pay any fees due and
      does not cure such failure within fifteen (15) days of receiving written notice of
      non-payment; (ii) materially violates the terms of this Agreement; or (iii) engages in
      illegal activity through the Platform.
    </li>
    <li>
      <strong>Termination by Organization.</strong> The Organization may terminate this
      Agreement immediately if Chareety materially breaches this Agreement and fails to cure
      such breach within thirty (30) days of receiving written notice specifying the nature
      of the breach.
    </li>
  </ol>

  <!-- SECTION 14: EFFECT OF TERMINATION -->
  <h2 id="effect-termination">14. Effect of Termination</h2>

  <ol type="a">
    <li>
      <strong>Wind-Down Period.</strong> Upon termination, there shall be a thirty (30) day
      wind-down period during which the Organization may continue to access the Platform in a
      read-only capacity for the purpose of exporting data.
    </li>
    <li>
      <strong>Data Export.</strong> During the wind-down period, Chareety will provide the
      Organization with a data export of its Donor Data in CSV and/or JSON format upon
      written request.
    </li>
    <li>
      <strong>Spoke Deletion.</strong> Upon expiration of the wind-down period, the
      Organization's Spoke Project and all data contained therein will be permanently deleted.
    </li>
    <li>
      <strong>Hub Records.</strong> Donation records retained in the hub database for the
      purpose of providing donors with cross-organization donation history via MyChareety
      (as described in Section 6) shall survive termination. These records are maintained
      for the benefit of donors and do not constitute continued processing of Organization
      data.
    </li>
    <li>
      <strong>Outstanding Fees.</strong> Termination does not relieve the Organization of its
      obligation to pay any fees accrued prior to the effective date of termination.
    </li>
    <li>
      <strong>Surviving Provisions.</strong> The following sections shall survive termination
      of this Agreement: Section 7 (Confidentiality), Section 8 (Intellectual Property),
      Section 10 (Limitation of Liability), Section 11 (Indemnification), and this
      Section 14.
    </li>
  </ol>

  <!-- SECTION 15: SUPPORT AND SERVICE LEVEL -->
  <h2 id="support-sla">15. Support and Service Level</h2>

  <ol type="a">
    <li>
      <strong>Partner-Referred Organizations.</strong> Organizations that were referred to the
      Platform by a Chareety partner shall receive primary support through their referring
      partner.
    </li>
    <li>
      <strong>Direct Sign-Up Organizations.</strong> Organizations that signed up directly
      shall receive email support at
      <a href="mailto:support@chareety.com">support&#64;chareety.com</a>. Chareety will use
      best efforts to respond to support inquiries within two (2) business days.
    </li>
    <li>
      <strong>Uptime Target.</strong> Chareety targets 99.5% Platform availability, measured
      on a monthly basis. This target excludes scheduled maintenance windows and events of
      force majeure.
    </li>
    <li>
      <strong>No Phone Support.</strong> Chareety does not provide phone-based support. All
      support communications shall be conducted via email.
    </li>
    <li>
      <strong>Scheduled Maintenance.</strong> Chareety will communicate scheduled maintenance
      windows via email to the Organization's registered administrator with reasonable advance
      notice.
    </li>
  </ol>

  <!-- SECTION 16: MODIFICATIONS TO AGREEMENT -->
  <h2 id="modifications">16. Modifications to Agreement</h2>

  <ol type="a">
    <li>
      <strong>Right to Modify.</strong> Chareety reserves the right to modify this Agreement
      at any time. Chareety will provide the Organization with at least thirty (30) days'
      prior written notice of any modifications.
    </li>
    <li>
      <strong>Acceptance by Continued Use.</strong> Continued use of the Platform after the
      expiration of the thirty (30) day notice period constitutes the Organization's
      acceptance of the modified terms.
    </li>
    <li>
      <strong>Material Changes.</strong> For modifications that materially alter the
      Organization's rights or obligations under this Agreement, Chareety will require
      affirmative consent from the Organization before such changes take effect.
    </li>
  </ol>

  <!-- SECTION 17: GOVERNING LAW AND DISPUTE RESOLUTION -->
  <h2 id="governing-law">17. Governing Law and Dispute Resolution</h2>

  <ol type="a">
    <li>
      <strong>Governing Law.</strong> This Agreement shall be governed by and construed in
      accordance with the laws of the State of Illinois, without regard to its conflict of
      laws principles.
    </li>
    <li>
      <strong>Informal Resolution.</strong> Before initiating any formal dispute resolution
      proceedings, the parties shall attempt to resolve any dispute arising out of or relating
      to this Agreement through informal negotiation for a period of thirty (30) days
      following written notice of the dispute.
    </li>
    <li>
      <strong>Binding Arbitration.</strong> If the parties are unable to resolve a dispute
      through informal negotiation, the dispute shall be submitted to binding arbitration
      administered by the American Arbitration Association (AAA) under its Commercial
      Arbitration Rules. The arbitration shall be conducted by a single arbitrator.
    </li>
    <li>
      <strong>Venue.</strong> The seat of arbitration and the exclusive venue for any legal
      proceedings shall be DuPage County, Illinois.
    </li>
    <li>
      <strong>Small Claims Exception.</strong> Either party may bring an individual action in
      small claims court for disputes within the jurisdictional limits of such court.
    </li>
    <li>
      <strong>No Class Actions.</strong> All disputes shall be resolved on an individual basis.
      Neither party shall participate in a class action, class arbitration, or other
      representative proceeding.
    </li>
  </ol>

  <!-- SECTION 18: GENERAL PROVISIONS -->
  <h2 id="general-provisions">18. General Provisions</h2>

  <ol type="a">
    <li>
      <strong>Entire Agreement.</strong> This Agreement, together with any exhibits, addenda,
      and Order Forms, constitutes the entire agreement between the parties with respect to
      the subject matter hereof and supersedes all prior and contemporaneous agreements,
      representations, and understandings, whether written or oral.
    </li>
    <li>
      <strong>Severability.</strong> If any provision of this Agreement is held to be invalid,
      illegal, or unenforceable, the remaining provisions shall continue in full force and
      effect. The invalid provision shall be modified to the minimum extent necessary to make
      it valid and enforceable.
    </li>
    <li>
      <strong>Waiver.</strong> The failure of either party to enforce any right or provision
      of this Agreement shall not constitute a waiver of such right or provision. A waiver
      of any provision shall be effective only if in writing and signed by the waiving party.
    </li>
    <li>
      <strong>Assignment.</strong> Chareety may assign this Agreement or any of its rights
      or obligations hereunder without the Organization's consent, including in connection
      with a merger, acquisition, or sale of all or substantially all of its assets. The
      Organization may not assign this Agreement without Chareety's prior written consent.
    </li>
    <li>
      <strong>Force Majeure.</strong> Neither party shall be liable for any failure or delay
      in performing its obligations under this Agreement due to causes beyond its reasonable
      control, including but not limited to acts of God, natural disasters, pandemics, war,
      terrorism, riots, government actions, power failures, internet or telecommunications
      failures, or third-party service provider outages.
    </li>
    <li>
      <strong>Notices.</strong> All notices required or permitted under this Agreement shall
      be sent via email to the registered email address on file for each party. Notices are
      deemed delivered upon successful transmission.
    </li>
    <li>
      <strong>Independent Contractors.</strong> The parties are independent contractors. Nothing
      in this Agreement shall be construed to create a partnership, joint venture, agency, or
      employment relationship between the parties.
    </li>
  </ol>

  <hr>

  <!-- EXHIBIT A -->
  <h2 id="exhibit-a">Exhibit A &mdash; Pricing Schedule</h2>

  <p>The following pricing applies to all Organizations using the Platform, based on their selected service tier.</p>

  <h3>Service Tiers</h3>

  <table class="msa-table">
    <thead>
      <tr>
        <th>Item</th>
        <th>Free</th>
        <th>Premium</th>
        <th>Enterprise</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>Platform base fee</td>
        <td>$0/month</td>
        <td>$0/month</td>
        <td>Custom</td>
      </tr>
      <tr>
        <td>One-time digital donations</td>
        <td>Stripe fees + 0.5% + $0.10</td>
        <td>Stripe fees + 0.5% + $0.10</td>
        <td>Contact sales</td>
      </tr>
      <tr>
        <td>Recurring subscriptions</td>
        <td>Stripe fees + 1%</td>
        <td>Stripe fees + 1%</td>
        <td>Contact sales</td>
      </tr>
      <tr>
        <td>Offline donation records</td>
        <td>First 200 free/month</td>
        <td>First 200 free, then $0.10 each</td>
        <td>Unlimited</td>
      </tr>
      <tr>
        <td>Communications</td>
        <td>First 200 free/month</td>
        <td>First 200 free, then $0.005 each</td>
        <td>Unlimited</td>
      </tr>
      <tr>
        <td colspan="4" style="font-size: 0.9em; color: #555; padding-top: 0;">
          Includes email broadcasts, pledge reminders, receipt re-sends, and tax documents.
          Automatic first-time donation receipts are free.
        </td>
      </tr>
      <tr>
        <td>SIM connectivity</td>
        <td>Not available</td>
        <td>$10/month per device</td>
        <td>$10/month per device</td>
      </tr>
      <tr>
        <td>Hardware &mdash; first device</td>
        <td>$399 (intro) / $499 (regular)</td>
        <td>$399 (intro) / $499 (regular)</td>
        <td>$399 (intro) / $499 (regular)</td>
      </tr>
      <tr>
        <td>Hardware &mdash; additional devices</td>
        <td>$299/unit</td>
        <td>$299/unit</td>
        <td>$299/unit</td>
      </tr>
      <tr>
        <td>On-site setup</td>
        <td>$100/visit</td>
        <td>$100/visit</td>
        <td>$100/visit</td>
      </tr>
      <tr>
        <td>Donor mobile app</td>
        <td>Chareety branded</td>
        <td>Chareety branded</td>
        <td>Full white-labeling</td>
      </tr>
    </tbody>
  </table>

  <h3>Payment Terms</h3>

  <ol type="a">
    <li>
      <strong>Monthly Billing.</strong> Platform usage fees are billed monthly in arrears via
      Stripe auto-charge to the payment method on file.
    </li>
    <li>
      <strong>Hardware and Setup.</strong> Hardware purchases and on-site setup fees are billed
      at the time of order and are non-refundable.
    </li>
    <li>
      <strong>Stripe Fees.</strong> Stripe payment processing fees are passed through directly
      from Stripe at Stripe's then-current published rates. Chareety does not mark up Stripe
      processing fees.
    </li>
    <li>
      <strong>Pricing Changes.</strong> Chareety may update pricing with no less than thirty
      (30) days' prior written notice, as described in Section 5(c) of this Agreement.
    </li>
    <li>
      <strong>Enterprise Pricing.</strong> Enterprise tier pricing is as specified in the
      Organization's separate Order Form and may differ from the standard pricing set forth
      in this Exhibit A.
    </li>
  </ol>

  <hr>

  <!-- EXHIBIT B -->
  <h2 id="exhibit-b">Exhibit B &mdash; Data Processing Addendum (DPA)</h2>

  <p>
    This Data Processing Addendum ("DPA") is incorporated into and forms part of the
    Master Service Agreement between Chareety and the Organization. In the event of a
    conflict between this DPA and the Agreement, this DPA shall prevail with respect to
    data processing matters.
  </p>

  <h3>B.1. Scope</h3>

  <p>
    This DPA governs Chareety's processing of Donor Data on behalf of the Organization
    in connection with the Services. Processing activities include the collection, storage,
    retrieval, analysis, transmission, and deletion of Donor Data as necessary to provide
    the Services.
  </p>

  <h3>B.2. Roles and Responsibilities</h3>

  <ol type="a">
    <li>
      <strong>Organization as Data Controller.</strong> The Organization acts as the Data
      Controller and determines the purposes and means of processing Donor Data.
    </li>
    <li>
      <strong>Chareety as Data Processor.</strong> Chareety acts as the Data Processor and
      processes Donor Data only as instructed by the Organization and as necessary to provide
      the Services described in the Agreement.
    </li>
  </ol>

  <h3>B.3. Categories of Data Processed</h3>

  <p>Chareety processes the following categories of Donor Data on behalf of the Organization:</p>

  <ul>
    <li>Donor names, email addresses, phone numbers, and mailing addresses</li>
    <li>Donation amounts, dates, payment methods, and transaction frequency</li>
    <li>Subscription and recurring donation preferences</li>
    <li>Tax receipt records and acknowledgment history</li>
    <li>Communication preferences and broadcast opt-in/opt-out status</li>
  </ul>

  <h3>B.4. Authorized Sub-Processors</h3>

  <p>
    The Organization authorizes Chareety to engage the following sub-processors for the
    purposes described below. Chareety shall ensure that each sub-processor is bound by
    data protection obligations no less protective than those set forth in this DPA.
  </p>

  <table class="msa-table">
    <thead>
      <tr>
        <th>Sub-Processor</th>
        <th>Purpose</th>
        <th>Data Processed</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>Google Cloud / Firebase</td>
        <td>Infrastructure, database, authentication, and storage</td>
        <td>All platform data</td>
      </tr>
      <tr>
        <td>Stripe</td>
        <td>Payment processing</td>
        <td>Payment card data, bank account details</td>
      </tr>
      <tr>
        <td>Amazon Web Services (SES)</td>
        <td>Transactional email delivery</td>
        <td>Email addresses, email content</td>
      </tr>
      <tr>
        <td>Brevo</td>
        <td>Email and SMS broadcast campaigns</td>
        <td>Email addresses, phone numbers, message content</td>
      </tr>
      <tr>
        <td>Hologram</td>
        <td>SIM connectivity for kiosk devices</td>
        <td>SIM identifiers, usage data</td>
      </tr>
    </tbody>
  </table>

  <p>
    Chareety will notify the Organization in writing before engaging any new sub-processor.
    The Organization may object to a new sub-processor within fifteen (15) days of receiving
    such notice. If the Organization objects and the parties cannot reach a mutually
    acceptable resolution, the Organization may terminate this Agreement in accordance
    with Section 13.
  </p>

  <h3>B.5. Security Measures</h3>

  <p>
    Chareety implements and maintains the following technical and organizational security
    measures to protect Donor Data:
  </p>

  <ul>
    <li>
      <strong>Encryption.</strong> All data is encrypted in transit using TLS and at rest
      using Google Cloud's default encryption mechanisms.
    </li>
    <li>
      <strong>Data Isolation.</strong> Each Organization's data is logically isolated in a
      separate Firebase project (Spoke Project), with its own database, authentication
      system, and security rules.
    </li>
    <li>
      <strong>App Check.</strong> Firebase App Check with reCAPTCHA Enterprise is enabled
      to verify that requests originate from legitimate application instances.
    </li>
    <li>
      <strong>Access Control.</strong> Firestore security rules implement a three-tier access
      control model (public, protected, and confidential) to enforce per-document permissions.
    </li>
    <li>
      <strong>Payment Security.</strong> Full credit card numbers are never stored on
      Chareety's systems. Payment processing is handled entirely by Stripe in accordance
      with PCI DSS requirements.
    </li>
    <li>
      <strong>Personnel Access.</strong> Access to Donor Data is limited to authorized
      Chareety personnel who require access to perform their duties in support of the
      Services.
    </li>
  </ul>

  <h3>B.6. Data Breach Notification</h3>

  <ol type="a">
    <li>
      <strong>Notification Timeline.</strong> Chareety will notify the Organization without
      undue delay, and in any event within seventy-two (72) hours, of becoming aware of a
      security breach affecting Donor Data.
    </li>
    <li>
      <strong>Notification Content.</strong> The notification shall include, to the extent
      known: (i) the nature and scope of the breach; (ii) the categories and approximate
      number of data subjects affected; (iii) the likely consequences of the breach; and
      (iv) the measures taken or proposed to address the breach.
    </li>
    <li>
      <strong>Cooperation.</strong> Chareety will cooperate with the Organization's breach
      response efforts, including assisting with notifications to affected individuals and
      regulatory authorities as required by applicable law.
    </li>
  </ol>

  <h3>B.7. Data Subject Rights</h3>

  <ol type="a">
    <li>
      Chareety will assist the Organization in responding to requests from donors exercising
      their rights under applicable data protection laws, including rights of access,
      correction, deletion, and data portability.
    </li>
    <li>
      Upon receiving a deletion request from the Organization, Chareety will promptly delete
      the specified Donor Data from the Organization's Spoke Project. Hub donation records
      retained for MyChareety (as described in Section 6 of the Agreement) are subject to
      the donor's direct request to Chareety.
    </li>
  </ol>

  <h3>B.8. Data Return and Deletion</h3>

  <ol type="a">
    <li>
      <strong>Data Export.</strong> Upon termination of the Agreement, Chareety will provide
      the Organization with a complete export of its Donor Data in CSV and/or JSON format
      during the thirty (30) day wind-down period described in Section 14 of the Agreement.
    </li>
    <li>
      <strong>Spoke Deletion.</strong> After the expiration of the wind-down period, the
      Organization's Spoke Project and all data contained therein will be permanently and
      irreversibly deleted.
    </li>
    <li>
      <strong>Hub Record Retention.</strong> Hub donation records retained for the MyChareety
      donor portal (as described in Section 6 of the Agreement) shall survive termination.
    </li>
    <li>
      <strong>Legal Retention.</strong> Financial records and transaction data may be retained
      by Chareety for up to seven (7) years following termination as required for tax
      reporting, legal compliance, and audit purposes.
    </li>
  </ol>

  <h3>B.9. Audit Rights</h3>

  <ol type="a">
    <li>
      The Organization may request documentation of Chareety's security practices and data
      processing procedures no more than once per calendar year.
    </li>
    <li>
      Chareety will respond to such requests within thirty (30) days by providing relevant
      documentation, including security policies, incident response procedures, and
      sub-processor agreements (with confidential commercial terms redacted).
    </li>
  </ol>

  <h3>B.10. Duration</h3>

  <p>
    This DPA shall remain in effect for the duration of the Agreement and for as long as
    Chareety processes Donor Data on behalf of the Organization, including any retention
    periods described in Section B.8 above.
  </p>

  <hr>

  <p class="msa-footer">
    <strong>xoomtag llc, doing business as Chareety</strong><br>
    Bartlett, IL 60103<br>
    <a href="mailto:support@chareety.com">support&#64;chareety.com</a><br><br>
    <em>Last updated: May 2026</em>
  </p>

</div>
